
import { memo, useMemo, useState } from 'react'
import Link from 'next/link'
import { DeviceGroupHeader } from '@/components/library/DeviceGroupHeader'
import { Icon } from '@/components/ui/Icon'
import {
  CATEGORY_META,
  DEVICE_CATEGORIES,
  distinctBrands,
  groupByCategory,
  partitionGroups,
  type DeviceCategory,
} from '@/lib/categories'
import { searchIndexFrom, searchDevices } from '@/lib/search'
import { formatFormFactor, metaLine } from '@/lib/signals'
import { useUiMode } from '@/components/workspace/UiModeProvider'
import { btn, cx, iconBtn, input, inputSm, tag, tagTone } from '@/lib/ui'
import type { DevicePaletteDTO } from '@/lib/types'

export const DEVICE_DRAG_TYPE = 'application/routr-device'

function PalettePanelInner({
  devices,
  unitCounts,
  favouriteIds,
  recentIds,
  libraryPath,
  onAdd,
  onCollapse,
}: {
  devices: DevicePaletteDTO[]
  /** deviceId → how many units the workspace owns. */
  unitCounts: Map<string, number>
  /** Starred in the library; pinned under "Favourites". */
  favouriteIds: ReadonlySet<string>
  /** Placed from this browser recently, newest first; pinned under "Recent". */
  recentIds: readonly string[]
  libraryPath: string
  /** Click-to-add fallback for touch devices, where HTML5 drag is unavailable. */
  onAdd: (device: DevicePaletteDTO) => void
  /** Shrinks the panel to its rail. */
  onCollapse: () => void
}) {
  const { advanced } = useUiMode()
  const [query, setQuery] = useState('')
  const [ownedOnly, setOwnedOnly] = useState(false)
  const [categoryFilter, setCategoryFilter] = useState<'all' | DeviceCategory>('all')
  const [brandFilter, setBrandFilter] = useState<'all' | string>('all')
  const ownedCount = useMemo(
    () => devices.filter((device) => (unitCounts.get(device.id) ?? 0) > 0).length,
    [devices, unitCounts],
  )

  const index = useMemo(() => searchIndexFrom(devices), [devices])
  const hits = useMemo(() => {
    const pool = devices.filter((device) => {
      if (ownedOnly && (unitCounts.get(device.id) ?? 0) === 0) return false
      if (categoryFilter !== 'all' && device.category !== categoryFilter) return false
      if (brandFilter !== 'all' && device.manufacturer !== brandFilter) return false
      return true
    })
    // Gear you actually own floats to the top of its group.
    return searchDevices(pool, index, query).sort(
      (a, b) =>
        Number((unitCounts.get(b.device.id) ?? 0) > 0) - Number((unitCounts.get(a.device.id) ?? 0) > 0),
    )
  }, [devices, index, query, ownedOnly, categoryFilter, brandFilter, unitCounts])
  const filtered = useMemo(() => hits.map((hit) => hit.device), [hits])
  const hints = useMemo(
    () => new Map(hits.flatMap((hit) => (hit.hint ? [[hit.device.id, hit.hint] as const] : []))),
    [hits],
  )
  const searching = query.trim() !== ''
  // Pinned groups only while browsing: a search result must list each device
  // once, and Recent/Favourites would otherwise sit beside its category.
  const groups = useMemo(
    () => (searching ? groupByCategory(filtered) : partitionGroups(filtered, { favouriteIds, recentIds })),
    [filtered, searching, favouriteIds, recentIds],
  )
  const brands = useMemo(() => distinctBrands(devices), [devices])
  const categories = useMemo(() => {
    const present = new Set(devices.map((device) => device.category))
    return DEVICE_CATEGORIES.filter((category) => present.has(category))
  }, [devices])
  const filtersActive = searching || categoryFilter !== 'all' || brandFilter !== 'all' || ownedOnly

  function clearFilters() {
    setQuery('')
    setCategoryFilter('all')
    setBrandFilter('all')
    setOwnedOnly(false)
  }

  return (
    <aside className="glass hidden min-h-0 w-64 shrink-0 flex-col rounded-[var(--radius-panel)] md:flex">
      <div className="shrink-0 space-y-2.5 border-b border-[var(--border)] p-3.5">
        <div className="flex items-center justify-between">
          {/* "Equipment" here too: the rail, the page title and this panel are
              one thing, and calling it Library in a third place made a reader
              wonder whether it was a fourth. */}
          <h2 className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted">Equipment</h2>
          <button
            type="button"
            onClick={onCollapse}
            aria-label="Hide equipment"
            aria-expanded={true}
            title="Hide equipment ([)"
            className={cx(iconBtn, '-mr-1 h-7 w-7')}
          >
            <Icon name="chevronLeft" className="h-4 w-4" />
          </button>
        </div>
        <div className="relative">
          <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
          <input
            className={cx(input, 'pl-9')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search equipment…"
            aria-label="Search equipment library"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select
            aria-label="Filter palette by category"
            className={cx(inputSm, 'w-full min-w-0')}
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value as 'all' | DeviceCategory)}
          >
            <option value="all">All types</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {CATEGORY_META[category].label}
              </option>
            ))}
          </select>
          <select
            aria-label="Filter palette by brand"
            className={cx(inputSm, 'w-full min-w-0')}
            value={brandFilter}
            onChange={(event) => setBrandFilter(event.target.value)}
          >
            <option value="all">All brands</option>
            {brands.map((brand) => (
              <option key={brand} value={brand}>
                {brand}
              </option>
            ))}
          </select>
        </div>
        {ownedCount > 0 && (
          <label className="flex items-center gap-2 text-[11px] text-muted">
            <input
              type="checkbox"
              checked={ownedOnly}
              onChange={(event) => setOwnedOnly(event.target.checked)}
              className="h-3.5 w-3.5 accent-[var(--ink)]"
            />
            Only gear we own ({ownedCount})
          </label>
        )}
      </div>

      <ul className="min-h-0 flex-1 overflow-y-auto p-2">
        {groups.map(([group, items]) => (
          <li key={group}>
            <DeviceGroupHeader group={group} count={items.length} surface="glass" />
            <ul>
              {items.map((device) => {
                const { inputs, outputs, bidirectional } = device
                const isComputer = device.category === 'COMPUTER'
                const filled = device.filledSlots
                const hint = hints.get(device.id)
                return (
                  <li key={device.id}>
                    <button
                      type="button"
                      draggable
                      onDragStart={(event) => {
                        event.dataTransfer.setData(DEVICE_DRAG_TYPE, device.id)
                        event.dataTransfer.effectAllowed = 'move'
                      }}
                      onClick={() => onAdd(device)}
                      title="Drag onto the canvas, or click to drop it in the middle"
                      className="w-full cursor-grab rounded-xl px-3 py-2 text-left transition-all duration-150 hover:bg-surface/85 hover:shadow-[var(--shadow-card)] active:cursor-grabbing"
                    >
                      <p className="flex items-center gap-1.5 text-xs font-medium">
                        <span className="truncate">{device.model}</span>
                        {(unitCounts.get(device.id) ?? 0) > 0 && (
                          <span
                            className={cx(tag, tagTone.success, 'shrink-0 font-mono')}
                            title="Units in your inventory"
                          >
                            ×{unitCounts.get(device.id)}
                          </span>
                        )}
                      </p>
                      <p className="truncate text-[11px] text-muted">
                        {device.manufacturer} · {device.name}
                      </p>
                      <p className="mt-0.5 font-mono text-[10px] text-muted/80">
                        {metaLine(
                          // Rack units and "bidi" mean nothing to a podcaster,
                          // and neither changes what they can do with the row.
                          advanced && formatFormFactor(device.formFactorRU),
                          advanced
                            ? `${inputs}/${outputs}`
                            : `${inputs} in · ${outputs} out`,
                          bidirectional > 0 &&
                            (advanced ? `${bidirectional} bidi` : `${bidirectional} either way`),
                          isComputer && (device.slots > 0 ? `${filled}/${device.slots} slots` : 'No slots'),
                        )}
                      </p>
                      {/* Why a port-only match is here. Hidden from the accessible
                          name so role queries by model keep matching one row. */}
                      {hint && (
                        <p aria-hidden="true" className="mt-0.5 truncate font-mono text-[10px] text-accent">
                          matches: {hint}
                        </p>
                      )}
                    </button>
                  </li>
                )
              })}
            </ul>
          </li>
        ))}

        {devices.length === 0 && (
          <li className="px-3 py-8 text-center text-xs text-muted">
            No equipment yet.{' '}
            <Link href={libraryPath} className="text-accent underline underline-offset-2">
              Add some to the library
            </Link>{' '}
            first.
          </li>
        )}

        {devices.length > 0 && filtered.length === 0 && (
          <li className="px-3 py-8 text-center text-xs text-muted">
            {query ? `Nothing matches “${query}”.` : 'Nothing matches the current filters.'}
            {filtersActive && (
              <button type="button" onClick={clearFilters} className={cx(btn.sm, btn.ghost, 'mt-2')}>
                Clear filters
              </button>
            )}
          </li>
        )}
      </ul>

      <p className="shrink-0 border-t border-[var(--border)] px-3.5 py-2.5 text-[10px] leading-relaxed text-muted">
        Drag a device onto the canvas, then pull a cable from an output handle to an input.
      </p>
    </aside>
  )
}

/**
 * Memoised: the canvas re-renders on every drag frame, and without this the
 * whole 355-row equipment list re-rendered and re-sorted with it — on the
 * two-column tablet layout, competing directly with the drag.
 */
export const PalettePanel = memo(PalettePanelInner)
